import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { supabase } from '@/integrations/supabase/client';
import { addMinutes, isAfter } from 'date-fns';
import { getShopDayLabel, getTechDayHours, getTechShiftBoundaryUtc } from './shopTime';
import { calculateWallMinutes } from './canonicalTimeMath';
// Core-100 R4 wave 3 (CORE-BE B): the one chain-membership test (sequence_index defaults to 1).
import { isPartOfRolloverChain } from './rolloverChainMembership';
// W4b (field test 2026-10-17): an UNSTARTED ticket past the end of day is a scheduling decision, never the started-
// work carry. The canonical virginity test routes it to the decision prompt (virgin-eod-exceeded), not the
// 'eod-exceeded' carry modal (DECISION_LEDGER §1; the server raises the human decision from the lane walk).
import {
  classifyEodConflict,
  eodConflictEventName,
  type EodConflictKind,
} from './virginEodSchedulingConflict';

interface EODCheckResult {
  exceeds: boolean;
  inBufferZone?: boolean;
  rolloverMinutes?: number;
  techName?: string;
  techEOD?: string;
  /** Forwarded so callers that re-dispatch the eod-exceeded event (e.g.
   *  TicketTimerControls' resume-anyway flow at TicketTimerControls.tsx:292
   *  which spreads `...result`) propagate the ticket type. The
   *  EODRolloverModal hides "Convert to Rollover" when this is 'Waiter'. */
  ticketType?: string | null;
  /** W4b: which EOD condition this is — the started-work carry, or an unstarted ticket's scheduling decision. */
  conflictKind?: EodConflictKind;
}

/**
 * Checks if a ticket's optifinish time exceeds the technician's EOD + willingness buffer
 * Dispatches 'eod-exceeded' event if violation detected
 * 
 * @param ticketId - The ticket to check
 * @param predictedMinutes - Total predicted minutes for the ticket
 * @param startTime - ISO string of ticket start time
 * @param techId - Assigned technician ID
 * @param customerName - Customer name for the modal
 * @param additionalMinutes - Additional minutes to add (e.g., pause time, break time)
 * @returns EODCheckResult indicating if EOD is exceeded
 */
export async function checkEODViolation(
  ticketId: string,
  predictedMinutes: number,
  startTime: string,
  techId: string,
  customerName: string,
  additionalMinutes: number = 0
): Promise<EODCheckResult> {
  // Skip EOD checks for tickets that are already part of a rollover chain
  // Once a ticket has been explicitly converted to a rollover, we don't want
  // to keep nagging the user with additional EOD modals for that chain.
  // Also fetch type so the dispatched event can flag Waiters and let the
  // EODRolloverModal hide the "Convert to Rollover" option (Waiters must
  // not be silently rolled — see eodRolloverHandler.ts guard).
  let ticketType: string | null = null;
  // W4b: unknown start state is treated as STARTED only when the row says so; a failed read keeps the legacy route.
  let conflictKind: EodConflictKind = 'started_work_rollover';
  try {
    const { data: existingTicket } = await supabase
      .from('tickets')
      .select('id, parent_ticket_id, sequence_index, split_reason, rollover_of_ticket_id, type, actually_started')
      .eq('id', ticketId)
      .single();

    const isRolloverChain = isPartOfRolloverChain(existingTicket);

    if (isRolloverChain) {
      return { exceeds: false };
    }
    ticketType = (existingTicket as any)?.type ?? null;
    if (existingTicket) conflictKind = classifyEodConflict(existingTicket as any);
  } catch (precheckError) {
    console.warn('EOD precheck error (continuing anyway):', precheckError);
  }

  try {
    // Fetch technician info (including per-day hours)
    const { data: tech, error: techError } = await supabase
      .from('technicians')
      .select('id, name, shift_start, shift_end, hours, efficiency_percentage, buffer_percentage, recurring_breaks, non_recurring_breaks')
      .eq('id', techId)
      .single();

    if (techError || !tech) {
      console.error('Technician not found for EOD check:', techError);
      return { exceeds: false };
    }

    // Fetch shop culture settings and timezone
    const { data: shopProfile, error: profileError } = await supabase
      .from('shop_profile')
      .select('shop_culture, time_zone')
      .limit(1)
      .maybeSingle();

    if (profileError) {
      console.error('Shop profile not found for EOD check:', profileError);
      return { exceeds: false };
    }

    const shopCulture = (shopProfile?.shop_culture as any) || {};
    const willingnessRollPastEOD = shopCulture.willingness_roll_past_eod || 0;
    const shopTimezone = shopProfile?.time_zone || 'America/New_York';

    // Calculate optifinish time (including any additional minutes like pause time)
    const efficiency = tech.efficiency_percentage || 100;
    const buffer = resolvePlanningBufferPct(tech.buffer_percentage);
    const optiTime = calculateWallMinutes(predictedMinutes, efficiency, buffer);
    
    const ticketStart = new Date(startTime);
    
    // Break-aware finish calculation
    const boardDateStr = getShopDayLabel(startTime, shopTimezone);
    const techBreaks = [
      ...((tech.recurring_breaks as any[] || []) as Array<{ start: string; end: string }>),
      ...((tech.non_recurring_breaks as any[] || []).filter((b: any) => b.date === boardDateStr) as Array<{ start: string; end: string }>),
    ];
    const breaksParsed = techBreaks.map((b: { start: string; end: string }) => {
      const [sh, sm] = b.start.split(':').map(Number);
      const [eh, em] = b.end.split(':').map(Number);
      return { start: sh * 60 + (sm || 0), end: eh * 60 + (em || 0) };
    });
    
    // Use shop timezone to get start minutes, then calculate break-aware finish
    const { getMinutesInShopTZ } = await import('./timezoneMath');
    const startMin = getMinutesInShopTZ(startTime, shopTimezone);
    const { calculateTicketOptiFinish } = await import('./canonicalTimeMath');
    const finishMin = calculateTicketOptiFinish(startMin, optiTime + additionalMinutes, breaksParsed);
    const { makeUTCFromShopMinutes } = await import('./timezoneMath');
    const optiFinish = makeUTCFromShopMinutes(boardDateStr, finishMin, shopTimezone);

    // Use per-day hours (with legacy fallback) and shop timezone
    const techWithTypedHours = { ...tech, hours: tech.hours as Record<string, { open: string; close: string }> | null };
    const techWithTypedHours2 = { ...tech, hours: tech.hours as Record<string, { open: string; close: string }> | null };
    const dayHours = getTechDayHours(techWithTypedHours2, boardDateStr);
    const shiftEndTime = dayHours?.close || tech.shift_end;
    const shiftEnd = getTechShiftBoundaryUtc(boardDateStr, shiftEndTime, shopTimezone);
    const maxAllowedEnd = addMinutes(shiftEnd, willingnessRollPastEOD);

    // Check if in buffer zone (between shift_end and maxAllowedEnd)
    if (isAfter(optiFinish, shiftEnd) && !isAfter(optiFinish, maxAllowedEnd)) {
      const rolloverMinutes = Math.ceil((optiFinish.getTime() - shiftEnd.getTime()) / 60000);
      
      // Dispatch buffer zone warning event (toast will handle one-time display)
      window.dispatchEvent(new CustomEvent('eod-buffer-warning', {
        detail: {
          ticketId,
          customerName,
          techName: tech.name,
          techEOD: tech.shift_end,
          rolloverMinutes,
          ticketType,
        }
      }));

      return {
        exceeds: false,
        inBufferZone: true,
        rolloverMinutes,
        techName: tech.name,
        techEOD: tech.shift_end,
        ticketType,
      };
    }

    // Check if optifinish exceeds allowed EOD (past buffer)
    if (isAfter(optiFinish, maxAllowedEnd)) {
      const rolloverMinutes = Math.ceil((optiFinish.getTime() - shiftEnd.getTime()) / 60000);
      
      // W4b: an UNSTARTED ticket goes to the decision prompt (virgin-eod-exceeded: reschedule / reassign — the
      // component has no carry action; the server decision panel holds approve / overtime). Only STARTED work
      // reaches the 'eod-exceeded' carry modal. ticketType lets EODRolloverModal hide "Convert to Rollover" for
      // Waiters (rolling a Waiter breaks the customer's promised pickup; refused server-side too).
      if (conflictKind === 'virgin_scheduling_conflict') {
        window.dispatchEvent(new CustomEvent(eodConflictEventName(conflictKind), {
          detail: {
            ticketId,
            customerName,
            techName: tech.name,
            techEOD: tech.shift_end,
            minutesPastShiftEnd: rolloverMinutes,
            ticketType,
          }
        }));
      } else {
        window.dispatchEvent(new CustomEvent('eod-exceeded', {
          detail: {
            ticketId,
            customerName,
            techName: tech.name,
            techEOD: tech.shift_end,
            rolloverMinutes,
            ticketType,
          }
        }));
      }

      return {
        exceeds: true,
        rolloverMinutes,
        techName: tech.name,
        techEOD: tech.shift_end,
        ticketType,
        conflictKind,
      };
    }

    return { exceeds: false };
  } catch (error) {
    console.error('EOD check error:', error);
    return { exceeds: false };
  }
}

/**
 * Checks all tickets for a technician on a given date for EOD violations
 * Useful after bulk operations or schedule recalculations
 */
export async function checkTechnicianEODViolations(
  techId: string,
  dateStr: string
): Promise<void> {
  try {
    const { data: tickets } = await supabase
      .from('tickets')
      .select('id, customer_name, start_time, predicted_minutes')
      .eq('assigned_tech_id', techId)
      .eq('scheduled_date', dateStr)
      .or('status.eq.Active,and(status.eq.Paused,actually_started.eq.true,bay_id.not.is.null)')
      .order('start_time', { ascending: true });

    if (!tickets || tickets.length === 0) return;

    // Check each ticket (pause time monitoring is handled separately by useEODMonitor hook)
    for (const ticket of tickets) {
      await checkEODViolation(
        ticket.id,
        ticket.predicted_minutes,
        ticket.start_time,
        techId,
        ticket.customer_name || 'Unknown Customer'
      );
    }
  } catch (error) {
    console.error('Bulk EOD check error:', error);
  }
}
